import {
  cardDesignFillFormWithStyles,
  cardDesignInitialValues,
  isoToDate,
} from "@";
import { attributeDurationsOptions } from "./options";
import {
  CardDesignFormValues,
  Project,
  ProjectAttribute,
  ProjectFormValues,
  ProjectVerificationLimitationResponse,
} from "../types";

// Function to get the name of the duration based on its value
export const durationName = (
  item: string | number | undefined,
): string | undefined => {
  const projectAttributeDuration = attributeDurationsOptions.find(
    (attribute) => attribute.value == item,
  );
  return projectAttributeDuration?.name;
};

// Function to fill the form with project data
export function fillForm(item: Project): ProjectFormValues {
  const certificateDesigns = [
    cardDesignFillFormWithStyles(item.endowmentCertificateDesign),
  ];

  const classificationsIds = item?.classifications?.map((el) => el.id) || [];
  const projectBankAccountsIds =
    item?.projectBankAccounts?.map((el) => el.bankAccountId) || [];

  const projectAttributes: ProjectAttribute[] =
    item?.projectAttributes?.map((attribute) => ({
      id: attribute.id,
      title: attribute.title,
      order: attribute.order,
      numberOfBeneficiariesNames: attribute.numberOfBeneficiariesNames || 0,
      price:
        (attribute as unknown as { amount: number }).amount || attribute.price,
      duration:
        (attribute as unknown as { deductionPeriod: string }).deductionPeriod ||
        attribute.duration,
      endingTime:
        (attribute as unknown as { deductionEndTime: string })
          .deductionEndTime || attribute.endingTime,
      verificationStartDate: attribute.verificationStartDate,
      verificationEndDate: attribute.verificationEndDate,
    })) || [];

  const projectVerificationValues =
    item?.projectVerificationLimitation?.map(
      (limitation: ProjectVerificationLimitationResponse) => ({
        ...limitation,
        verificationStageNumber: limitation.stageNumber,
        verificationStageName: limitation.stageName,
        smsMessage: limitation.smsMessage,
      }),
    ) || [];

  return {
    ...item,
    enableVerification: item.enableVerification,
    projectAttributes: projectAttributes,
    bankAccountsIds: [],
    endowmentCertificateDesign: certificateDesigns,
    projectType: item.category,
    themeId: item?.themeId,
    themeUrl: item?.themeUrl,
    endsTo: isoToDate(item.endsTo),
    giftProjectId: item.giftProject?.id,
    classificationIds: classificationsIds,
    startsFrom: isoToDate(item.startsFrom),
    image: null,
    projectDescriptionImagesUrls: item?.projectDescriptionImagesUrls?.[0],
    projectDescriptionImages: null,
    projectLicence: null,
    certificate: null,
    certificateView: null,
    bankAccountsIdsArray: projectBankAccountsIds,
    projectVerificationLimitations: projectVerificationValues,
    projectLicenceCodes: item.projectLicenceCodes || [],
  } as unknown as ProjectFormValues;
}

// Initialize gift card design values
export const getCardDesignInitValues = (
  design: CardDesignFormValues[] | undefined,
  defaultX: number | string,
  defaultY: number | string,
  disclaimer: number,
): CardDesignFormValues => {
  const cardDesign = design?.find((d) => d.disclaimer === disclaimer);
  if (cardDesign) {
    return cardDesign;
  } else {
    return {
      ...cardDesignInitialValues,
      x: parseInt(String(defaultX)),
      y: parseInt(String(defaultY)),
      disclaimer: disclaimer,
    };
  }
};

export function projectPayload(data: ProjectFormValues) {
  return {
    id: data.id,
    name: data.name,
    title: data.title,
    slug: data.slug,
    shortDescription: data.shortDescription,
    summary: data.summary,
    projectDetails: data.projectDetails,
    projectType: data.projectType,
    order: data.order,

    // Images
    image: data.image,
    projectDescriptionImages: data.projectDescriptionImages,
    imageUrl: data.imageUrl,
    imageAlt: data.imageAlt,
    projectLicence: data.projectLicence,
    projectLicenceUrl: data.projectLicenceUrl,
    certificate: data.certificate,
    certificateUrl: data.certificateUrl,
    certificateView: data.certificateView,

    // Dates & Amounts
    startsFrom: data.startsFrom,
    endsTo: data.endsTo,
    allRequiredAmount: data.allRequiredAmount,
    currentPaidAmount: data.currentPaidAmount,
    minimumOpenDonationAmount: data.minimumOpenDonationAmount,

    // Settings
    classificationIds: data.classificationIds,
    giftProjectId: data.giftProjectId,
    projectAccountShowingOption: data.projectAccountShowingOption,
    bankAccountsIds: data.bankAccountsIds,
    bankAccountsIdsArray: data.bankAccountsIdsArray,

    // Toggles
    showOnHomePage: data.showOnHomePage,
    showForFastDonation: data.showForFastDonation,
    showProgressBar: data.showProgressBar,
    isFastPaymentActive: data.isFastPaymentActive,
    haveCertificate: data.haveCertificate,
    hideProjectFromStore: data.hideProjectFromStore,
    notifyWhenCompleted: data.notifyWhenCompleted,
    enableVerification: data.enableVerification,
    allowOpenDonation: data.allowOpenDonation,
    projectVisualEffect: data.projectVisualEffect,
    isOnBehalfOnProject: data.isOnBehalfOnProject,

    // Notification
    personEmailToNotify: data.personEmailToNotify,
    personPhoneToNotify: data.personPhoneToNotify,

    // Verification Settings
    // projectVerificationLimitations: data.projectVerificationLimitations,
    projectVerificationLimitation: data.projectVerificationLimitations,

    // Project Attributes
    projectAttributes: data.projectAttributes,

    // SEO
    seoTitle: data.seoTitle,
    seoKeywords: data.seoKeywords,
    seoDescription: data.seoDescription,

    // Adahi
    adahiProductId: data.adahiProductId,
    adahiRitualId: data.adahiRitualId,

    // Certificate Design
    endowmentCertificateDesign: data.endowmentCertificateDesign,

    // Customer Campaign
    customerSuggestedCampaignId: data.customerSuggestedCampaignId,
    customerSuggestedCampaignName: data.customerSuggestedCampaignName,
    websiteUrl: data.websiteUrl,

    // Licences
    projectLicenceCodes: data.projectLicenceCodes,

    // themeId
    themeId: data.themeId,
  };
}
